import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import { SITE, IMG } from "@/lib/site";
import AnimatedCounter from "@/components/AnimatedCounter";
import HeroParallaxBg from "@/components/HeroParallaxBg";
import ScrollCue from "@/components/cinema/ScrollCue";
import GlassDrops from "@/components/cinema/GlassDrops";
import { Reveal, LineReveal } from "@/components/cinema/Reveal";

const STATS = [
  { k: 24, suffix: "/7", l: "Live dispatch line" },
  { k: 12, suffix: "+", l: "GTA cities served" },
  { k: 60, suffix: "", l: "Min · GTA response target" },
];

export default function HeroChapter() {
  return (
    <section className="relative hero-cinema hero-live text-white overflow-hidden min-h-[calc(100vh-72px)] flex flex-col" data-testid="hero">
      <HeroParallaxBg src={IMG.hero} />
      <div className="absolute inset-0 hero-veil" aria-hidden />
      <GlassDrops />
      <div className="grain absolute inset-0" aria-hidden />

      <div className="container-gaza relative flex-1 flex flex-col items-center justify-center text-center py-16 md:py-20">
        <Reveal delay={0.1} y={16}>
          <div className="live-pill mb-10"><span className="dot"></span><span>Crews on call now</span></div>
        </Reveal>
        <LineReveal
          as="h1"
          delay={0.2}
          className="display-chunky text-[2.8rem] sm:text-6xl md:text-[5.5rem] lg:text-[6.5rem] text-white"
          lines={["When disaster", "strikes,", <span className="text-[hsl(var(--gaza-crimson-bright))]">we move first.</span>]}
        />
        <Reveal delay={0.7}>
          <p className="text-xl md:text-2xl text-white/85 max-w-2xl leading-relaxed mt-10 font-light">
            Water. Fire. Storm. Mold. The moment it happens, the clock starts. <span className="text-white font-semibold">We pick up. We dispatch. We protect what matters.</span>
          </p>
        </Reveal>
        <Reveal delay={0.9}>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={SITE.phoneHref} className="btn-gold group" data-testid="hero-call-btn">
              <Phone className="w-4 h-4" /> Emergency Line · {SITE.phone}
            </a>
            <Link to="/services" className="btn-outline" data-testid="hero-services-btn">
              What we do <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={1.1}>
          <div className="mt-20 grid grid-cols-3 gap-8 max-w-2xl mx-auto">
            {STATS.map((s) => (
              <div key={s.l} className="border-t border-white/15 pt-5 text-left">
                <div className="mega-num text-5xl md:text-7xl text-[hsl(var(--gaza-crimson-bright))]">
                  <AnimatedCounter value={s.k} suffix={s.suffix} />
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/60 mt-3 font-extrabold">{s.l}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      <ScrollCue className="mx-auto mb-6" />

      <div className="relative border-t border-white/10 bg-[hsl(var(--gaza-teal-deep))]/60 overflow-hidden" data-testid="hero-marquee">
        <div className="py-5 relative">
          <div className="marquee-track flex items-center gap-10 text-white/65 text-sm whitespace-nowrap animate-[sweep_28s_linear_infinite]">
            {[...SITE.serviceAreas, ...SITE.serviceAreas].map((c, i) => (
              <span key={`${c}-${i}`} className="flex items-center gap-2 font-semibold"><span className="w-1.5 h-1.5 bg-[hsl(var(--bh-red-bright))] rounded-full" />{c}</span>
            ))}
          </div>
          <div className="marquee-label" data-testid="hero-marquee-label">
            <div className="container-gaza w-full">
              <span className="text-[10px] uppercase tracking-[0.28em] font-extrabold text-[hsl(var(--bh-red-bright))] relative z-10">Now responding</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
